import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import {
  fetchReferralHistory,
  fetchReferralLeaderboard,
  fetchReferralSummary,
} from '../services/referralService';

export const referralQueryKeys = {
  summary: (scope) => ['referral', scope, 'summary'],
  history: (scope) => ['referral', scope, 'history'],
  leaderboard: (scope, filter) => ['referral', scope, 'leaderboard', filter || 'all'],
};

function useReferralScope() {
  const { user } = useAuth();
  const profile = user?.dataset?.data || user || {};
  const id = profile?.id || profile?.user_id || 'current';
  const language = profile?.preferred_lang || profile?.content_language || 'EN';
  return `${id}:${language}`;
}

export function useReferralSummary() {
  const scope = useReferralScope();
  return useQuery({
    queryKey: referralQueryKeys.summary(scope),
    queryFn: ({ signal }) => fetchReferralSummary({ signal }),
    staleTime: 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function useReferralHistory(enabled) {
  const scope = useReferralScope();
  return useInfiniteQuery({
    queryKey: referralQueryKeys.history(scope),
    queryFn: ({ pageParam = 1, signal }) => fetchReferralHistory(pageParam, { signal }),
    initialPageParam: 1,
    getNextPageParam: (page) => page.pagination.currentPage < page.pagination.totalPages
      ? page.pagination.currentPage + 1 : undefined,
    enabled,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function useReferralLeaderboard(filter = null, enabled = true) {
  const scope = useReferralScope();
  return useInfiniteQuery({
    queryKey: referralQueryKeys.leaderboard(scope, filter),
    queryFn: ({ pageParam = 1, signal }) => fetchReferralLeaderboard(pageParam, filter, { signal }),
    initialPageParam: 1,
    enabled,
    getNextPageParam: (page) => page.pagination.currentPage < page.pagination.totalPages
      ? page.pagination.currentPage + 1 : undefined,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
